import { useEffect, useRef, useState } from 'react';
import { fieldRequired, type ReportTypeDef } from '@ni/shared';
import { useApi } from '../../api/app-context';
import { errorLine, validationSentence } from '../../api/errors';
import { AccountChoice } from '../accounts/account-choice';
import { EventChoice } from './event-choice';
import { PeoplePicker, type PickedPerson } from './people-picker';
import { Button } from '../ui/button';

const PROSE_MAX = 20_000;

/**
 * A report's inputs: the people and the type's prose fields, and on a new
 * report an optional title (reports design §2, owner rulings 4 and the title
 * rule). The same form serves New report and Change inputs.
 *
 * An account-scoped type (the travel brief) takes its protection account first,
 * on a new report only; the people are then found on that account alone, and
 * changing the account clears them. An existing report's account is fixed.
 *
 * A type that takes an event asks for it after the account, and a type whose
 * people come from the event (`max_people: 0`) has no people picker. An
 * existing report's event is fixed, as its account is.
 */
export function ReportInputsForm({
  type,
  initialPeople = [],
  initialInputs = {},
  fixedAccountId,
  initialAccountId = '',
  initialEventId = '',
  withEvent = true,
  withTitle,
  submitLabel,
  creditShort = null,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  type: ReportTypeDef;
  initialPeople?: PickedPerson[];
  initialInputs?: Record<string, string>;
  /** Change inputs on an account-scoped report: its account, not choosable. */
  fixedAccountId?: string | null;
  /** A new report opened from somewhere that already knows these (the event's Analysis tab). */
  initialAccountId?: string;
  initialEventId?: string;
  /** False on Change inputs: the event is fixed once the report exists. */
  withEvent?: boolean;
  withTitle: boolean;
  submitLabel: string;
  /** Why the submit is disabled: the balance cannot cover its price. */
  creditShort?: string | null;
  pending: boolean;
  error: unknown;
  onSubmit: (value: {
    title: string;
    accountId: string | null;
    eventId: string | null;
    people: PickedPerson[];
    inputs: Record<string, string>;
  }) => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState('');
  const [people, setPeople] = useState<PickedPerson[]>(initialPeople);
  const [inputs, setInputs] = useState<Record<string, string>>(initialInputs);
  const [chosenAccount, setChosenAccount] = useState(initialAccountId);
  const [chosenEvent, setChosenEvent] = useState(initialEventId);
  const accountId = type.account_scoped ? (fixedAccountId ?? chosenAccount) || null : null;
  const asksEvent = withEvent && type.event !== 'none';
  const eventId = asksEvent ? chosenEvent || null : null;
  const takesPeople = type.max_people > 0;
  const api = useApi();
  const hasEvent =
    eventId !== null || (!withEvent && type.event !== 'none' && initialEventId !== '');
  const missing = type.fields.some(
    (f) => fieldRequired(f, hasEvent) && !(inputs[f.key] ?? '').trim(),
  );
  /* Choosing an event for a type that picks its own people fills the picker
     with the event's attendees on the record; the office edits from there. */
  const latestEvent = useRef('');
  const chooseEvent = (id: string) => {
    setChosenEvent(id);
    latestEvent.current = id;
    if (!takesPeople || id === '') return;
    void api.getEventPeople(id).then(
      ({ people: attending }) =>
        // A slower answer for an event since changed is not this form's any more.
        latestEvent.current === id &&
        setPeople(
          attending
            .slice(0, type.max_people)
            .map((p) => ({ entity_id: p.entity_id, name: p.display_name })),
        ),
      () => {},
    );
  };
  const ready =
    /* A type that always has its event reads the list from it, so nobody on
       the record attending is still a report (the server's rule). */
    (!takesPeople || people.length > 0 || (type.event === 'required' && hasEvent)) &&
    !missing &&
    !pending &&
    creditShort === null &&
    (!type.account_scoped || accountId !== null) &&
    (!asksEvent || type.event !== 'required' || eventId !== null);
  /* Opened with the event already chosen (from the event's page), the people
     are filled once, as a choice would fill them. */
  useEffect(() => {
    if (withEvent && initialEventId && initialPeople.length === 0) chooseEvent(initialEventId);
    // Once, on opening: a later choice goes through `chooseEvent` itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form
      className="report-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) onSubmit({ title: title.trim(), accountId, eventId, people, inputs });
      }}
    >
      {type.account_scoped && fixedAccountId === undefined ? (
        <AccountChoice
          active
          value={chosenAccount}
          onChange={(id) => {
            setChosenAccount(id);
            setChosenEvent('');
            setPeople([]);
          }}
        />
      ) : null}
      {asksEvent && (!type.account_scoped || accountId !== null) ? (
        <EventChoice accountId={accountId} value={chosenEvent} onChange={chooseEvent} />
      ) : null}
      {takesPeople && (!type.account_scoped || accountId !== null) ? (
        <PeoplePicker
          label={type.people_label}
          value={people}
          onChange={setPeople}
          max={type.max_people}
          {...(accountId ? { accountId } : {})}
        />
      ) : null}

      {type.fields.map((field) => (
        <label className="field" key={field.key}>
          <span className="label">{field.label}</span>
          <span className="report-field-prompt">{field.prompt}</span>
          <textarea
            className="control"
            aria-label={field.label}
            rows={6}
            maxLength={PROSE_MAX}
            value={inputs[field.key] ?? ''}
            onChange={(e) => setInputs({ ...inputs, [field.key]: e.target.value })}
          />
        </label>
      ))}

      {withTitle ? (
        <label className="field">
          <span className="label">Title</span>
          <span className="report-field-prompt">Optional.</span>
          <input
            className="control"
            type="text"
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
      ) : null}

      {error ? <p role="alert">{validationSentence(error) ?? errorLine(error)}</p> : null}

      <div className="report-form-actions">
        <button type="submit" className="btn go" disabled={!ready}>
          {pending ? 'Starting…' : submitLabel}
        </button>
        {creditShort ? <span className="credit-short">{creditShort}</span> : null}
        {onCancel ? (
          <Button variant="act" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
